import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IChecklistTemplatesApi, IJobChecklistsApi } from '../api/contracts';
import * as types from '../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { Toast } from '../components/Toast';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { canEditJobChecklist, canTickJobChecklist } from '../utils/permissions';
import {
  checklistGroupNameOptions,
  checklistProgressLabel,
  checklistProgressOf,
  checklistStepTextError,
  checklistTemplateOptions,
  removeChecklistStep,
  replaceChecklistStep,
  requiredStepsWarning,
} from './jobChecklistUi';

/** Mobile-only 44px touch target; desktop sizing unchanged (same as JobDetailPage). */
const tap = ' min-h-[44px] sm:min-h-0';
const EMPTY: Omit<types.JobChecklist, 'jobId'> = { total: 0, completed: 0, requiredIncomplete: 0, groups: [] };

export type JobChecklistApi = IJobChecklistsApi & Pick<IChecklistTemplatesApi, 'listChecklistTemplates'>;

export interface JobChecklistSectionProps {
  api: JobChecklistApi;
  /**
   * The page's current Job. A new object (the page refreshed it, e.g. after a
   * work item add / edit / remove, which can add or remove template groups on
   * the server) reloads the checklist.
   */
  job: Pick<types.Job, 'id' | 'assignedTo' | 'checklist'>;
  user: types.User | null | undefined;
  /** The latest progress after every load or change (for the page's completion warning). */
  onProgress?: (progress: types.JobChecklistProgress) => void;
  /** Tick / untick only: no add, edit, remove or template (the technician job screen, item 42). */
  tickOnly?: boolean;
}

interface StepEdit { id: string; text: string; required: boolean }
interface NewStep { text: string; required: boolean; groupName: string }

/**
 * Job detail Checklist section (tracker item 44, phase 4). ADMIN /
 * OFFICE_MANAGER: tick, add, edit, remove and apply a template.
 * TECHNICIAN: tick / untick on jobs assigned to them only (read-only text);
 * anyone else sees nothing. The backend enforces the same rules (403) and
 * allows changes in every job status. Unticked required steps warn; they
 * never block anything.
 */
export function JobChecklistSection({ api, job, user, onProgress, tickOnly = false }: JobChecklistSectionProps) {
  const canTick = canTickJobChecklist(user, job);
  const canEdit = !tickOnly && canEditJobChecklist(user);
  const [checklist, setChecklist] = useState<types.JobChecklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingSteps, setPendingSteps] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<StepEdit | null>(null);
  const [adding, setAdding] = useState<NewStep | null>(null);
  const [picker, setPicker] = useState<{ templates: types.ChecklistTemplate[] | null; error: string | null; templateId: string } | null>(null);
  const loadSeq = useRef(0);
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  const show = useCallback((next: types.JobChecklist) => setChecklist(next), []);

  useEffect(() => {
    if (checklist) progressRef.current?.(checklistProgressOf(checklist));
  }, [checklist]);

  const notify = (kind: 'error' | 'success', message: string) => {
    if (kind === 'error') { setSuccess(null); setError(message); } else { setError(null); setSuccess(message); }
  };

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const fresh = await api.getJobChecklist(job.id);
      if (seq === loadSeq.current) show(fresh);
    } catch (err) {
      if (seq === loadSeq.current) setLoadError(getErrorMessage(err, 'Could not load the checklist'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [api, job.id, show]);

  useEffect(() => {
    if (!canTick) return;
    // The job response says it has no steps: skip the request.
    if (job.checklist && job.checklist.total === 0) {
      loadSeq.current += 1;
      setLoading(false);
      setLoadError(null);
      show({ jobId: job.id, ...EMPTY });
      return;
    }
    void load();
    // Reload whenever the page hands over a refreshed job.
  }, [canTick, job, load, show]);

  if (!canTick) return null;

  const failed = async (err: unknown, fallback: string) => {
    notify('error', getErrorMessage(err, fallback));
    // 404 / 403: the step or the assignment changed elsewhere; show the current list.
    const code = getErrorCode(err);
    if (code === 'NOT_FOUND' || code === 'FORBIDDEN') await load();
  };

  const toggle = async (step: types.JobChecklistStep) => {
    if (pendingSteps.has(step.id) || busy) return;
    setPendingSteps((current) => new Set(current).add(step.id));
    try {
      const saved = step.completed
        ? await api.uncompleteJobChecklistStep(job.id, step.id)
        : await api.completeJobChecklistStep(job.id, step.id);
      setChecklist((current) => (current ? replaceChecklistStep(current, saved) : current));
    } catch (err) {
      await failed(err, step.completed ? 'Could not untick the step' : 'Could not tick the step');
    } finally {
      setPendingSteps((current) => { const next = new Set(current); next.delete(step.id); return next; });
    }
  };

  const run = async (action: () => Promise<void>, fallback: string) => {
    if (busy) return;
    setBusy(true);
    try { await action(); } catch (err) { await failed(err, fallback); } finally { setBusy(false); }
  };

  const saveEdit = () => run(async () => {
    if (!editing || !checklist) return;
    const issue = checklistStepTextError(editing.text);
    if (issue) { notify('error', issue); return; }
    const saved = await api.updateJobChecklistStep(job.id, editing.id, { text: editing.text.trim(), required: editing.required });
    setChecklist((current) => (current ? replaceChecklistStep(current, saved) : current));
    setEditing(null);
    notify('success', 'Checklist step updated.');
  }, 'Could not update the step');

  const removeStep = (step: types.JobChecklistStep) => {
    if (busy || !checklist || !window.confirm(`Remove the step "${step.text}" from this job's checklist?`)) return;
    void run(async () => {
      await api.deleteJobChecklistStep(job.id, step.id);
      setChecklist((current) => (current ? removeChecklistStep(current, step.id) : current));
      if (editing?.id === step.id) setEditing(null);
      notify('success', 'Checklist step removed.');
    }, 'Could not remove the step');
  };

  const saveNew = () => run(async () => {
    if (!adding) return;
    const issue = checklistStepTextError(adding.text);
    if (issue) { notify('error', issue); return; }
    await api.addJobChecklistStep(job.id, {
      text: adding.text.trim(),
      required: adding.required,
      ...(adding.groupName && adding.groupName !== types.JOB_CHECKLIST_DEFAULT_GROUP ? { groupName: adding.groupName } : {}),
    });
    setAdding(null);
    // The server places the step (group and order): show its list.
    show(await api.getJobChecklist(job.id));
    notify('success', 'Checklist step added.');
  }, 'Could not add the step');

  const openPicker = async () => {
    setPicker({ templates: null, error: null, templateId: '' });
    try {
      const templates = await api.listChecklistTemplates({ active: true });
      setPicker((current) => (current ? { ...current, templates } : current));
    } catch (err) {
      setPicker((current) => (current ? { ...current, templates: [], error: getErrorMessage(err, 'Could not load checklist templates') } : current));
    }
  };

  const applyTemplate = () => run(async () => {
    if (!picker?.templateId) return;
    const result = await api.applyJobChecklistTemplate(job.id, picker.templateId);
    show(result.checklist);
    setPicker(null);
    notify('success', result.applied ? 'Template applied to the checklist.' : 'That template is already on this job; nothing changed.');
  }, 'Could not apply the template');

  const warning = checklist ? requiredStepsWarning(checklist.requiredIncomplete) : '';
  const templateOptions = picker?.templates ? checklistTemplateOptions(picker.templates, checklist) : [];

  return (
    <section aria-labelledby="job-checklist-heading" className="bg-white rounded-lg shadow p-5 sm:p-7">
      {error && <Toast kind="error">{error}</Toast>}
      {success && <Toast kind="success">{success}</Toast>}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 id="job-checklist-heading" className="text-lg font-semibold">
          Checklist
          {checklist && checklist.total > 0 && (
            <span className="ml-2 text-base font-normal text-gray-600" aria-label={`${checklist.completed} of ${checklist.total} steps done`} data-testid="checklist-progress">
              {checklistProgressLabel(checklist)}
            </span>
          )}
        </h2>
        {canEdit && checklist && (
          <div className="flex flex-wrap gap-3">
            {!adding && <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy} onClick={() => { setEditing(null); setAdding({ text: '', required: false, groupName: types.JOB_CHECKLIST_DEFAULT_GROUP }); }}>Add step</button>}
            {!picker && <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy} onClick={() => void openPicker()}>Apply template</button>}
          </div>
        )}
      </div>

      {loading && !checklist && <p role="status" className="text-gray-600">Loading checklist...</p>}
      {loadError && (
        <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {loadError}{' '}
          <button type="button" className={`text-blue-700 underline${tap}`} disabled={loading} onClick={() => void load()}>Retry</button>
        </div>
      )}

      {warning && (
        <p role="note" data-testid="checklist-required-warning" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {warning} This does not block completing the job.
        </p>
      )}

      {picker && (
        <div className="mb-4 rounded-lg border border-gray-200 p-4">
          {picker.templates === null ? <p role="status" className="text-sm text-gray-600">Loading templates...</p> : picker.error ? (
            <p role="alert" className="text-sm text-red-800">{picker.error}</p>
          ) : templateOptions.length === 0 ? (
            <p className="text-sm text-gray-600">No active checklist templates. Create one under Settings &gt; Checklist Templates.</p>
          ) : (
            <div>
              <label htmlFor="checklist-template" className={labelClass}>Template</label>
              <select id="checklist-template" className={`${inputClass} sm:max-w-md`} value={picker.templateId} disabled={busy} onChange={(event) => setPicker({ ...picker, templateId: event.target.value })}>
                <option value="">Select a template</option>
                {templateOptions.map((option) => <option key={option.id} value={option.id} disabled={option.onJob}>{option.label}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-600">Copies the template&apos;s steps onto this job as a new group. Later template edits do not change this job.</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-3">
            {templateOptions.length > 0 && <button type="button" className={`${primaryButtonClass}${tap}`} disabled={busy || !picker.templateId} onClick={() => void applyTemplate()}>Apply</button>}
            <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy} onClick={() => setPicker(null)}>Cancel</button>
          </div>
        </div>
      )}

      {checklist && checklist.groups.length === 0 && !adding && (
        <p className="text-gray-600">{canEdit ? 'No checklist steps yet. Add a step or apply a template; work items whose catalog item links a template add theirs automatically.' : 'No checklist steps on this job.'}</p>
      )}

      {checklist && checklist.groups.length > 0 && (
        <div className="space-y-5">
          {checklist.groups.map((group) => (
            <div key={`${group.sourceTemplateId ?? ''}:${group.groupName}`} role="group" aria-label={group.groupName}>
              <h3 className="flex flex-wrap items-baseline gap-2 font-semibold break-words">
                {group.groupName}
                <span className="text-sm font-normal text-gray-600">{checklistProgressLabel(group)}</span>
              </h3>
              <ul className="mt-2 divide-y divide-gray-100 border-y border-gray-100">
                {group.steps.map((step) => {
                  const pending = pendingSteps.has(step.id);
                  const isEditing = editing?.id === step.id;
                  return (
                    <li key={step.id} className="py-1" data-testid={`checklist-step-${step.id}`}>
                      {isEditing ? (
                        <div className="space-y-3 py-2">
                          <div>
                            <label htmlFor={`checklist-edit-${step.id}`} className={labelClass}>Step text</label>
                            <input id={`checklist-edit-${step.id}`} className={inputClass} value={editing.text} maxLength={types.CHECKLIST_STEP_TEXT_MAX} disabled={busy} onChange={(event) => setEditing({ ...editing, text: event.target.value })} />
                          </div>
                          <label className={`flex items-center gap-2 text-sm${tap}`}>
                            <input type="checkbox" className="h-5 w-5" checked={editing.required} disabled={busy} onChange={(event) => setEditing({ ...editing, required: event.target.checked })} />
                            Required
                          </label>
                          <div className="flex flex-wrap gap-3">
                            <button type="button" className={`${primaryButtonClass}${tap}`} disabled={busy} onClick={() => void saveEdit()}>Save step</button>
                            <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-between gap-x-3">
                          <label className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-3 py-1">
                            <input
                              type="checkbox"
                              className="h-5 w-5 shrink-0"
                              checked={step.completed}
                              disabled={pending || busy}
                              onChange={() => void toggle(step)}
                            />
                            <span className={`min-w-0 break-words ${step.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{step.text}</span>
                            {step.required && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">Required</span>}
                          </label>
                          {canEdit && (
                            <div className="flex gap-3">
                              <button type="button" className={`text-blue-700 underline${tap}`} disabled={busy} aria-label={`Edit step ${step.text}`} onClick={() => { setAdding(null); setEditing({ id: step.id, text: step.text, required: step.required }); }}>Edit</button>
                              <button type="button" className={`text-red-700 underline${tap}`} disabled={busy} aria-label={`Remove step ${step.text}`} onClick={() => removeStep(step)}>Remove</button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="mt-4 rounded-lg border border-gray-200 p-4 space-y-3">
          <div>
            <label htmlFor="checklist-new-text" className={labelClass}>New step</label>
            <input id="checklist-new-text" className={inputClass} value={adding.text} maxLength={types.CHECKLIST_STEP_TEXT_MAX} disabled={busy} onChange={(event) => setAdding({ ...adding, text: event.target.value })} />
          </div>
          <div>
            <label htmlFor="checklist-new-group" className={labelClass}>Group</label>
            <select id="checklist-new-group" className={`${inputClass} sm:max-w-md`} value={adding.groupName} disabled={busy} onChange={(event) => setAdding({ ...adding, groupName: event.target.value })}>
              {checklistGroupNameOptions(checklist).map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <p className="mt-1 text-xs text-gray-600">A step added to a template&apos;s group is removed with that group if its work item is removed while nothing in the group is ticked.</p>
          </div>
          <label className={`flex items-center gap-2 text-sm${tap}`}>
            <input type="checkbox" className="h-5 w-5" checked={adding.required} disabled={busy} onChange={(event) => setAdding({ ...adding, required: event.target.checked })} />
            Required
          </label>
          <div className="flex flex-wrap gap-3">
            <button type="button" className={`${primaryButtonClass}${tap}`} disabled={busy} onClick={() => void saveNew()}>Save step</button>
            <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy} onClick={() => setAdding(null)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
